import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { DocumentNode, useMutation } from '@apollo/client';
import { useQuery } from '@apollo/client';
import _ from 'lodash';

import makeStyles from '@mui/styles/makeStyles';
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Button,
  Tab,
  Tabs,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import TabPanel from '../../../../../components/TabPanel.js';

// core components
import styles from '../../../../../assets/jss/material-dashboard-pro-react/components/tasksStyle.js';
import iconPlusSvg from '../../../../../assets/img/icons/plus.svg';
import iconMinusSvg from '../../../../../assets/img/icons/minus.svg';
import { theme } from '../../../../../theme';
import { Clear } from '@mui/icons-material';
import twoWayArrow from '../../../../../assets/img/icons/two-way-arrow.png';
import TextFieldComp from './FieldComps/TextField.js';
import CheckboxComp from './FieldComps/Checkbox.js';
import { docuSignApi } from '../../api';
import { SendEnvelopeV2Variables } from '../../api/types.js';
import { useNotifications } from 'modules/notification';
import { logError } from 'utils/logger';

const fixedStyles = { ...styles } as any;
if (fixedStyles.tooltip && typeof fixedStyles.tooltip.lineBreak === 'string') {
  if (!['auto', 'normal', 'unset', undefined].includes(fixedStyles.tooltip.lineBreak)) {
    fixedStyles.tooltip.lineBreak = 'auto';
  }
}

interface MappingModalProps {
  handleClose: (reason?: 'cancel' | 'sent') => void;
  query: DocumentNode;
  variables: Record<string, any>;
  dataKey: string;
  vendorOpportunityId: string;
  templateId: string;
}

const useStyles = makeStyles((theme: any) => ({
  ...fixedStyles,
  ...styles,
  modalStyle: {
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    minWidth: 1200,
    '@media (max-width: 1200px)': {
      width: '120vw',
      minWidth: '120vw',
    },
  },
  dateSelect: {
    marginTop: '11px',
    width: '100%',
    '& label': {
      color: '#000000',
      fontSize: 14,
      fontWeight: 400,
    },
  },
  inputLabel: {
    transform: 'translate(14px, 12px) scale(1)',
  },
  labelNotMapped: {
    width: '100%',
    float: 'right',
    textAlign: 'right',
  },
  label: {
    fontSize: 14,
    fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
    fontWeight: 400,
    color: 'rgba(0, 0, 0, 0.6)',
  },
  textField: {
    margin: '8px 0 0',
    width: '100%',
    height: 40,
    '& input': {
      padding: 12,
      fontSize: 14,
    },
    '& fieldset:disabled': {
      borderColor: '#f44336',
    },
    '& .MuiOutlinedInput-input': {
      padding: '12px 24px',
    },
    '& fieldset legend span': {
      fontSize: 12,
      fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
      textTransform: 'none',
      fontWeight: 'normal',
    },
  },
  wrapperLoader: {
    display: 'flex',
    flexDirection: 'column',
    color: theme.palette.grey[500],
    width: '100%',
    alignItems: 'center',
    margin: '64px 0',
    '& .headerContainer': {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 32,
      '& h4': {
        marginLeft: 8,
      },
    },
    '& .progressLoader': {
      margin: 32,
      color: theme.palette.grey[500],
    },
  },
  selectStyles: {
    height: 40,
    '& .MuiOutlinedInput-input': {
      padding: '8px 24px',
    },
    '& fieldset legend span': {
      padding: 0,
      fontSize: '0.85em',
    },
  },
  selectLabel: {
    transform: 'translate(14px, 12px) scale(1)',
    display: '-webkit-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '-webkit-line-clamp': 1,
    '-webkit-box-orient': 'vertical',
  },
  collapseButton: {
    top: 0,
    right: 0,
    width: 50,
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 20,
    zIndex: 3,
    cursor: 'pointer',
    color: '#999',
  },
  icon: {
    width: 20,
    height: 20,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    display: 'inline-block',
    opacity: 0.3,
  },
  iconPlus: {
    backgroundImage: `url("${iconPlusSvg}")`,
  },
  iconMinus: {
    backgroundImage: `url("${iconMinusSvg}")`,
  },
}));

const DocuSignMappingModal: React.FC<MappingModalProps> = ({
  handleClose,
  query,
  variables,
  dataKey,
  vendorOpportunityId,
  templateId,
}) => {
  const classes: any = useStyles();
  const tableCellClasses = classnames(classes.tableCell);
  const [docuSignTabMappings, setDocuSignTabMappings] = useState<any>(null);
  const [loadingMappings, setLoadingMappings] = useState<any>(true);
  const [currentTabSelected, setCurrentTabSelected] = useState<any>('dlm');
  const { showNotification } = useNotifications();
  const [onSendEnvelope, { loading: isEnvelopeSending }] = useMutation<void, any>(docuSignApi.sendEnvelopeV2);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: string) => {
    setCurrentTabSelected(newValue);
  };

  const handleMappingValueChange = (newValue: any, label: any) => {
    const index = docuSignTabMappings.findIndex((el: any) => el.label === label);
    // Convert boolean values to strings for checkbox types
    if (docuSignTabMappings[index].type === 'CHECKBOX' && typeof newValue === 'boolean') {
      docuSignTabMappings[index].value = String(newValue);
    } else {
      docuSignTabMappings[index].value = newValue;
    }
    setDocuSignTabMappings([...docuSignTabMappings]);
  };

  const renderField = (docuSignTabMap: any) => {
    const type = docuSignTabMap.type;
    switch (type) {
      case 'PHONE_NUMBER':
        return (
          <TextFieldComp
            docuSignTabMap={docuSignTabMap}
            type={docuSignTabMap.type}
            index={docuSignTabMap.label}
            handleBlur={handleMappingValueChange}
            key={docuSignTabMap.label}
            inputProps={{}}
            mask="(999) 999-9999"
            setReference={() => {}}
            setReferenceToRequiredFunction={() => {}}
            setReferenceToErrorsValueFunction={() => {}}
          />
        );
      case 'CHECKBOX':
        return (
          <CheckboxComp
            docuSignTabMap={docuSignTabMap}
            index={docuSignTabMap.label}
            handleBlur={handleMappingValueChange}
            key={docuSignTabMap.label}
            setReference={() => {}}
            setReferenceToRequiredFunction={() => {}}
            setReferenceToErrorsValueFunction={() => {}}
          />
        );
      default:
        return (
          <TextFieldComp
            docuSignTabMap={docuSignTabMap}
            type={docuSignTabMap.type}
            index={docuSignTabMap.label}
            handleBlur={handleMappingValueChange}
            key={docuSignTabMap.label}
            inputProps={{}}
            setReference={() => {}}
            setReferenceToRequiredFunction={() => {}}
            setReferenceToErrorsValueFunction={() => {}}
          />
        );
    }
  };

  const handleSend = async () => {
    try {
      const inputDocuSignMapping = (docuSignTabMappings || []).map((tab: any) => {
        if (!tab || typeof tab !== 'object') return tab;
        const { required, __typename, trnsactSource, trnsactSourceDisplayName, ...rest } = tab;
        return rest;
      });

      await onSendEnvelope({
        variables: { VOId: vendorOpportunityId, input: { templateId, tabMappings: inputDocuSignMapping } },
      });

      handleClose('sent');
      showNotification('Successfully sent template!');
    } catch (err) {
      logError(err);
      showNotification('Something went wrong', { type: 'error' });
    }
  };

  const {
    data: queryData,
    loading: queryLoading,
    called: queryCalled,
  } = useQuery(query, {
    variables,
    fetchPolicy: 'no-cache',
    onError(error) {
      logError(error);
      showNotification('Failed to load DocuSign mappings', { type: 'error' });
      setLoadingMappings(false);
      handleClose('cancel');
    },
  });

  useEffect(() => {
    if (!queryLoading && queryCalled && queryData) {
      setDocuSignTabMappings(queryData[dataKey]);
      setLoadingMappings(false);
    }
  }, [queryLoading, queryCalled, queryData, dataKey]);

  if (loadingMappings) {
    return (
      <div className={classes.modalStyle}>
        <CircularProgress className="progressLoader" size={48} />
        <div className={classes.headerContainer}>
          <h4>Loading DocuSign Mappings...</h4>
        </div>
      </div>
    );
  }

  if (!docuSignTabMappings) {
    return null;
  }

  if (isEnvelopeSending) {
    return (
      <div className={classes.modalStyle}>
        <CircularProgress className="progressLoader" size={48} />
        <div className={classes.headerContainer}>
          <h4>Sending DocuSign Envelope...</h4>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.modalStyle}>
      <Tabs
        value={currentTabSelected}
        onChange={handleTabChange}
        variant="fullWidth"
        aria-label="full width tabs example"
        TabIndicatorProps={{
          style: {
            backgroundColor: (theme as any).palette.primary.main,
          },
        }}
      >
        <Tab
          value="dlm"
          label={'DocuSign tabs mapping'}
          style={{ color: currentTabSelected === 'dlm' ? (theme as any).palette.primary.main : '' }}
        />
      </Tabs>

      <TabPanel value={currentTabSelected} index="dlm">
        <div style={{ paddingBottom: 16 }} />
        <Table className={classes.table} style={{ marginBottom: 8 }}>
          <TableHead className={classes.tableHeader}>
            <TableCell
              style={{ fontSize: 16, fontWeight: 'bold', textAlign: 'center', marginRight: '8px' }}
              className={tableCellClasses}
              width="40%"
            >
              Trnsact Fields
            </TableCell>
            <TableCell style={{ fontSize: 16, fontWeight: 'bold' }} className={tableCellClasses} width="20%">
              {['']}
            </TableCell>
            <TableCell
              style={{ fontSize: 16, fontWeight: 'bold', marginRight: '8px' }}
              className={tableCellClasses}
              width="40%"
            >
              DocuSign Tabs
            </TableCell>
          </TableHead>
        </Table>
        <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {docuSignTabMappings &&
            docuSignTabMappings.map((docuSignTabMapping: any) => (
              <div key={docuSignTabMapping.label} style={{ marginBottom: 40, width: '98%' }}>
                <Table
                  className={classes.table}
                  style={{
                    display: 'table',
                  }}
                >
                  <TableBody>
                    <TableRow>
                      <TableCell className={tableCellClasses} width="40%">
                        <Box component="form" noValidate autoComplete="off">
                          {docuSignTabMapping.trnsactSource && (
                            <>
                              {docuSignTabMapping.type === 'CHECKBOX' ? (
                                <FormControlLabel
                                  style={{ marginTop: '16px', marginLeft: '-11px' }}
                                  control={
                                    <Checkbox
                                      checked={
                                        docuSignTabMapping.value === true ||
                                        docuSignTabMapping.value === 'true' ||
                                        docuSignTabMapping.value === 'Yes'
                                      }
                                      disabled={true}
                                      color="primary"
                                    />
                                  }
                                  label={
                                    (docuSignTabMapping.required ? '* ' : '') +
                                    docuSignTabMapping.trnsactSourceDisplayName
                                  }
                                  classes={{
                                    label: classes.label,
                                  }}
                                />
                              ) : (
                                <TextField
                                  className={classes.textField}
                                  disabled={true}
                                  fullWidth={true}
                                  label={
                                    (docuSignTabMapping.required ? '* ' : '') +
                                    docuSignTabMapping.trnsactSourceDisplayName
                                  }
                                  defaultValue={docuSignTabMapping.value}
                                  variant="outlined"
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            </>
                          )}
                          {!docuSignTabMapping.trnsactSource && (
                            <span className={classes.labelNotMapped}>No field target</span>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell
                        className={tableCellClasses}
                        width="10%"
                        padding-left="70px"
                        padding-top="24px"
                        align="center"
                      >
                        <img
                          style={{ marginBottom: 16 }}
                          src={twoWayArrow}
                          width="20px"
                          height="10px"
                          alt="Mapping arrow"
                        />
                      </TableCell>
                      <TableCell
                        className={tableCellClasses}
                        width="40%"
                        style={{ display: 'flex', width: '100%', alignItems: 'center' }}
                      >
                        {renderField(docuSignTabMapping)}
                        {!_.isEmpty(docuSignTabMapping.value) ? (
                          <IconButton
                            onClick={() => {
                              handleMappingValueChange(null, docuSignTabMapping.label);
                            }}
                            style={{ height: 'fit-content' }}
                            size="large"
                          >
                            <Clear fontSize="small" />
                          </IconButton>
                        ) : (
                          <div style={{ width: 48 }} />
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            ))}
        </div>
      </TabPanel>
      <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0', gap: 16 }}>
        <Button onClick={() => handleClose('cancel')} variant="outlined" disabled={isEnvelopeSending}>
          Cancel
        </Button>
        <Button color="primary" autoFocus onClick={handleSend}>
          Send
        </Button>
      </div>
    </div>
  );
};

export default DocuSignMappingModal;
